import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useGlobalStore } from '../stores/globalStore';

// Custom-event name used to broadcast in-tab preference changes so that
// multiple `usePreference` instances bound to the same key stay in sync
// when one of them writes a new value. The native `storage` event only
// fires in OTHER tabs, not the tab that performed the write — hence this.
const PREF_CHANGE_EVENT = 'dietech-pref-changed';
type PrefChangeDetail = { userId: number; key: string };

// ============================================================================
// User Preferences Hook
//
// Centralized localStorage-backed preference system keyed by userId so
// multiple users on the same machine don't conflict.
//
// Usage:
//   const { get, set, remove } = useUserPreferences();
//   const widths = get<Record<string, number>>('weightEntry.colWidths', {});
//   set('weightEntry.colWidths', { name: 200, weight: 100 });
//
// All keys are automatically namespaced under `dietech_prefs_{userId}_`.
// ============================================================================

const STORAGE_PREFIX = 'dietech_prefs';

/** Build the full localStorage key for a given user + preference key */
function storageKey(userId: number, key: string): string {
  return `${STORAGE_PREFIX}_${userId}_${key}`;
}

/**
 * Read a preference value from localStorage for the given user.
 * Returns `defaultValue` if not found or on parse error.
 */
export function getUserPref<T>(userId: number, key: string, defaultValue: T): T {
  if (userId <= 0) return defaultValue;
  try {
    const raw = localStorage.getItem(storageKey(userId, key));
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Write a preference value to localStorage for the given user. Also
 * dispatches an in-tab change event so any other `usePreference` instance
 * watching the same key picks up the new value immediately.
 */
export function setUserPref<T>(userId: number, key: string, value: T): void {
  if (userId <= 0) return;
  try {
    localStorage.setItem(storageKey(userId, key), JSON.stringify(value));
    window.dispatchEvent(
      new CustomEvent<PrefChangeDetail>(PREF_CHANGE_EVENT, { detail: { userId, key } }),
    );
  } catch {
    // localStorage full or unavailable - silently ignore
  }
}

/**
 * Remove a preference value from localStorage for the given user.
 */
export function removeUserPref(userId: number, key: string): void {
  if (userId <= 0) return;
  try {
    localStorage.removeItem(storageKey(userId, key));
  } catch {
    // ignore
  }
}

// ============================================================================
// Well-known preference keys (for type safety and discoverability)
// ============================================================================
export const PREF_KEYS = {
  // Theme
  DARK_MODE: 'theme.darkMode',
  // Three-size era (small / medium / large). Still written for older builds;
  // read only to carry a choice over (stores/themeStore.ts).
  THEME_SIZE: 'theme.size',
  // Four sizes (small / medium / large / xlarge), since 2026-10-21.
  THEME_SIZE_V2: 'theme.size.v2',
  // UI 2026 refresh opt-in / opt-out (see theme/ui2026Flag.ts)
  UI_2026: 'theme.ui2026',
  // High contrast accessibility theme (see theme/highContrast.css)
  HIGH_CONTRAST: 'theme.highContrast',
  // Where shared form fields put their label (see stores/themeStore.ts)
  LABEL_PLACEMENT: 'theme.labelPlacement',
  // App font (see theme/appFonts.ts)
  FONT: 'theme.font',

  // Reports
  REPORTS_VIEW_MODE: 'reports.viewMode',
  REPORTS_DASHBOARD_LAYOUT: 'reports.dashboardLayout',

  // Weight Entry
  // v2: the old key holds junk. `onOptionChanged` was wired to persist on
  // EVERY option change, not just a resize, so whatever widths the DevExtreme
  // grid happened to be holding mid-edit were saved over the real ones - and then seeded back
  // in, overriding the designer widths.
  WEIGHT_COL_WIDTHS: 'weightEntry.colWidths.v2',

  // Production Menu View Options
  PROD_MENU_VIEW_OPTIONS: 'prodMenu.viewOptions',

  // Residents grid
  RESIDENTS_COL_OVERRIDES: (facilityId: number) => `residents.colOverrides.${facilityId}`,

  // The welcome tour shown the first time someone signs in (components/welcome). Holds
  // { version, shownAt, outcome }. It replaced the one-page "welcome.dismissed" dialog.
  WELCOME_TOUR: 'welcome.wizard',
  // The What's New pop-up (components/welcome/WhatsNewNotice): the id of the newest release note
  // (src/releaseNotes) this user has seen or been spared.
  // These two are also kept on the server, per user (hooks/useServerUserState); the API accepts
  // only them (UserStateController.AllowedKeys). Renaming one means changing both.
  WHATS_NEW_SEEN: 'whatsNew.seen',

  // Sidebar collapsed state
  SIDEBAR_COLLAPSED: 'layout.sidebarCollapsed',

  // Pages pinned to Home's panel (page keys, pin order) and the pages opened recently (newest
  // first). See navigation/navPins.ts.
  NAV_PINNED: 'nav.pinned',
  NAV_RECENT: 'nav.recent',

  // Dashboard selected hours
  DASHBOARD_HOURS: 'dashboard.selectedHours',

  // Info box collapsed state
  INFO_BOX_COLLAPSED: 'infoBox.collapsed',

  // Hide all info boxes globally
  HIDE_INFO_BOXES: 'infoBox.hideAll',

  // Production Menu - power mode (compact ribbon, keyboard-driven)
  PROD_MENU_POWER_MODE: 'prodMenu.powerMode',

  // Production Menu - bottom drawer height
  PROD_MENU_DRAWER_HEIGHT: 'prodMenu.drawerHeight',

  // Selective Touch display settings
  ST_BUTTON_STYLE: 'st.buttonStyle',
  ST_CARD_DENSITY: 'st.cardDensity',
  ST_STATUS_STYLE: 'st.statusStyle',
  ST_UNIT_COLORS: 'st.unitColors',
} as const;

// ============================================================================
// React Hook
// ============================================================================

interface UserPreferences {
  /** Get a preference value, returning defaultValue if not set */
  get: <T>(key: string, defaultValue: T) => T;
  /** Set a preference value */
  set: <T>(key: string, value: T) => void;
  /** Remove a preference value */
  remove: (key: string) => void;
  /** Current user ID (0 = not resolved yet) */
  userId: number;
}

/**
 * Hook that provides user-scoped preference read/write backed by localStorage.
 *
 * All values are keyed by the current userId from globalStore, so preferences
 * are isolated per user even on shared machines.
 */
export function useUserPreferences(): UserPreferences {
  const userId = useGlobalStore((s) => s.userId);

  const get = useCallback(
    <T>(key: string, defaultValue: T): T => getUserPref(userId, key, defaultValue),
    [userId],
  );

  const set = useCallback(
    <T>(key: string, value: T): void => setUserPref(userId, key, value),
    [userId],
  );

  const remove = useCallback(
    (key: string): void => removeUserPref(userId, key),
    [userId],
  );

  return useMemo(() => ({ get, set, remove, userId }), [get, set, remove, userId]);
}

// ============================================================================
// Convenience hook: single preference with React state
// ============================================================================

/**
 * Hook that manages a single preference as React state.
 * Updates to the value are automatically persisted to localStorage.
 *
 * Usage:
 *   const [viewMode, setViewMode] = usePreference('reports.viewMode', 'card');
 */
export function usePreference<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const userId = useGlobalStore((s) => s.userId);

  const [value, setValueState] = useState<T>(() => getUserPref(userId, key, defaultValue));

  // Track userId changes - re-read from storage if user switches
  const prevUserIdRef = useRef(userId);
  if (prevUserIdRef.current !== userId && userId > 0) {
    prevUserIdRef.current = userId;
    const stored = getUserPref(userId, key, defaultValue);
    // Only update state if value actually changed (avoid unnecessary re-renders)
    if (JSON.stringify(stored) !== JSON.stringify(value)) {
      setValueState(stored);
    }
  }

  const setValue = useCallback(
    (newValue: T) => {
      setValueState(newValue);
      setUserPref(userId, key, newValue);
    },
    [userId, key],
  );

  // Sync this instance's state when another component writes the same
  // preference key. `setUserPref` dispatches `PREF_CHANGE_EVENT` on write,
  // and we re-read from storage when a matching event fires. Without this,
  // each `usePreference` call holds an independent snapshot and writes
  // from one component don't propagate to others in the same tab.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PrefChangeDetail>).detail;
      if (!detail) return;
      if (detail.key !== key) return;
      if (detail.userId !== userId) return;
      const stored = getUserPref(userId, key, defaultValue);
      setValueState(stored);
    };
    window.addEventListener(PREF_CHANGE_EVENT, handler);
    return () => window.removeEventListener(PREF_CHANGE_EVENT, handler);
    // `defaultValue` is captured by closure; re-subscribing on every
    // render due to a fresh object literal would be wasteful and isn't
    // necessary — staleness here only matters if the key has never been
    // written, which is a non-issue in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key]);

  return [value, setValue];
}
